import React, { useRef, useContext, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import DataGrid from "../../components/DataGrid";
import Toggle from "../../components/DataGrid/toggle.js";
import { Context } from "../../Reducers/Store";

export default function UsuarioList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef();
  const [state] = useContext(Context);

  const filtroInicial = searchParams.get("filtro") || "";
  const [ativoFiltro, setAtivoFiltro] = useState(searchParams.get("ativo") ?? "true");
  const escopoFiltro = searchParams.get("escopo") || "";

  const isSistema = state?.profile?.tipo_usuario === "Sistema";

  const getTitulo = () => {
    if (escopoFiltro === "todos") {
      if (ativoFiltro === "true") return "Todos os Usuários Ativos";
      if (ativoFiltro === "false") return "Todos os Usuários Inativos";
      return "Todos os Usuários";
    }
    return "Usuários do Sistema";
  };

  const handleToggleStatus = async (id, ativo) => {
    try {
      await axios.put(`${process.env.REACT_APP_API}/usuario/${id}/status`, {
        id,
        ativo: !ativo
      });
      gridRef.current?.refresh();
    } catch (error) {
      console.error("Erro ao alterar status:", error);
    }
  };

  const handleAtivoChange = (valor) => {
    setAtivoFiltro(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("ativo", valor);
    } else {
      params.delete("ativo");
    }
    setSearchParams(params);
  };

  const getColumns = () => {
    const baseColumns = [
      {
        field: "nome",
        header: "Nome",
        sortable: true,
      },
      {
        field: "email",
        header: "E-mail",
        sortable: true,
      },
    ];

    // Coluna de Conta apenas para usuário Sistema
    if (isSistema) {
      baseColumns.push({
        field: "contaNome",
        header: "Conta",
        render: (row) => (
          <span className={row.contaNome !== "Sistema" ? "text-gray-800" : "text-purple-600 font-medium"}>
            {row.contaNome}
          </span>
        ),
      });
    }

    baseColumns.push({
      key: "tipoUsuario",
      header: "Tipo",
      className: "text-center",
      headerClassName: "text-center",
      width: "120px",
      render: (row) => {
        const tipos = {
          1: { label: "Sistema", className: "bg-purple-100 text-purple-700" },
          2: { label: "Admin", className: "bg-blue-100 text-blue-700" },
          3: { label: "Usuário", className: "bg-gray-100 text-gray-700" },
        };
        const tipo = tipos[row.tipoUsuario] || tipos[3];
        return (
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${tipo.className}`}>
            {tipo.label}
          </span>
        );
      },
    });

    baseColumns.push({
      key: "ativo",
      header: "Ativo",
      className: "text-center",
      headerClassName: "text-center",
      width: "100px",
      render: (row) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Toggle
            value={row.ativo}
            onChange={() => handleToggleStatus(row.id, row.ativo)}
          />
        </div>
      ),
    });

    baseColumns.push({
      key: "acoes",
      header: "Ações",
      width: "100px",
      className: "text-center",
      headerClassName: "text-center",
      render: (row) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/usuarios/${row.id}`);
          }}
          className="p-2 text-gray-600 hover:text-slate-900 hover:bg-gray-100 rounded"
          title="Editar"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      ),
    });

    return baseColumns;
  };

  // Filtros externos para passar ao DataGrid
  const externalFilters = useMemo(() => {
    const filters = {};
    if (ativoFiltro !== "") filters.ativo = ativoFiltro;
    if (escopoFiltro) filters.escopo = escopoFiltro;
    return filters;
  }, [ativoFiltro, escopoFiltro]);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-bold">{getTitulo()}</h1>
          {(ativoFiltro !== "true" || escopoFiltro) && (
            <button
              onClick={() => {
                setAtivoFiltro("true");
                navigate("/usuarios?ativo=true");
              }}
              className="text-sm text-blue-600 hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {/* Filtro de Ativo */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Status:</span>
            <select
              value={ativoFiltro}
              onChange={(e) => handleAtivoChange(e.target.value)}
              className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
              <option value="">Todos</option>
            </select>
          </div>
        </div>
      </div>

      <DataGrid
        ref={gridRef}
        endpoint={`${process.env.REACT_APP_API}/usuario`}
        columns={getColumns()}
        filters={externalFilters}
        initialFilter={filtroInicial}
        emptyMessage="Nenhum usuário encontrado"
        emptyAction={
          <button
            onClick={() => navigate("/usuarios/novo")}
            className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
          >
            Cadastrar primeiro usuário
          </button>
        }
        headerAction={
          <button
            onClick={() => navigate("/usuarios/novo")}
            className="px-4 py-2 text-sm rounded bg-slate-900 text-white hover:bg-slate-800"
          >
            Novo Usuário
          </button>
        }
        onRowClick={(row) => navigate(`/usuarios/${row.id}`)}
      />
    </div>
  );
}